// src/services/classFavorites.js
// Firestore helpers for class favorites (hearts/likes)
// Collection: classFavorites
// Doc ID: ${userId}_${classId}
// Schema: { userId, classId, createdAt }
// likeCount is maintained on the liveClasses doc via atomic increment/decrement

import { db, firebaseEnabled } from './firebase';
import { doc, getDoc, collection, query, where, getDocs, increment } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc, deleteDoc, updateDoc } from './firestoreWrite';

// ── Set favorite state directly — no pre-read, so the write queues immediately. ──
// The caller passes `shouldFavorite` (derived from optimistic UI state), which lets us
// skip the initial getDoc round-trip. This matters when the user can refresh mid-call
// (e.g. post-class overlay → tap favorite → pull-to-refresh): without the pre-read,
// the Firestore SDK queues the write immediately and it persists across the refresh.
// With the pre-read, a refresh that lands during the ~200-500ms read window would
// abort the call before the write was ever queued. Firestore's deleteDoc on a
// non-existent doc is a no-op; setDoc on an existing doc is idempotent overwrite —
// so skipping the read is safe in both directions.
export async function setFavoriteState(userId, classId, shouldFavorite) {
    if (!firebaseEnabled || !db || !userId || !classId) return shouldFavorite;
    try {
        const favId = `${userId}_${classId}`;
        const favRef = doc(db, 'classFavorites', favId);
        const delta = shouldFavorite ? 1 : -1;
        if (shouldFavorite) {
            await setDoc(favRef, { userId, classId, createdAt: new Date().toISOString() });
        } else {
            await deleteDoc(favRef);
        }
        // Increment/decrement the authoritative count on the liveClasses doc.
        try { await updateDoc(doc(db, 'liveClasses', classId), { likeCount: increment(delta) }); } catch (e) { /* class may be deleted */ }
        // Dual-write to the matching classRecordings doc when the VOD has already been
        // created (vodAssetId set via D-059). Keeps the On Demand count in sync with
        // any live-window tap that lands AFTER the Mux webhook fired its initial
        // count-copy. Fire-and-forget — a failure here never blocks UI because the
        // liveClasses count is still authoritative.
        try {
            const classSnap = await getDoc(doc(db, 'liveClasses', classId));
            const vodAssetId = classSnap.exists() ? classSnap.data().vodAssetId : null;
            if (vodAssetId) {
                updateDoc(doc(db, 'classRecordings', vodAssetId), { likeCount: increment(delta) }).catch(() => {});
            }
        } catch (e) { /* best-effort sync — liveClasses remains authoritative */ }
        return shouldFavorite;
    } catch (e) {
        console.error('Error setting favorite state:', e);
        return !shouldFavorite; // write failed — report the opposite so caller can revert optimistic UI
    }
}

// ── Backward-compat wrapper — reads current state then delegates. ──
// Kept so existing call sites don't break; prefer `setFavoriteState` for hot paths.
export async function toggleFavorite(userId, classId) {
    if (!firebaseEnabled || !db || !userId || !classId) return false;
    try {
        const favId = `${userId}_${classId}`;
        const favRef = doc(db, 'classFavorites', favId);
        const existing = await getDoc(favRef);
        return await setFavoriteState(userId, classId, !existing.exists());
    } catch (e) {
        console.error('Error toggling favorite:', e);
        return false;
    }
}

// ── Get all classIds the user has favorited ──
export async function getUserFavorites(userId) {
    if (!firebaseEnabled || !db || !userId) return new Set();
    try {
        const q = query(collection(db, 'classFavorites'), where('userId', '==', userId));
        const snap = await getDocs(q);
        return new Set(snap.docs.map((d) => d.data().classId));
    } catch (e) {
        console.error('Error fetching favorites:', e);
        return new Set();
    }
}
